import React, { useContext, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Keyboard, TouchableOpacity, View } from 'react-native'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Text } from '@components/base'
import { ScrollableAvoidKeyboard } from '@components'
import { SecureInput } from '@components/inputs/SecureInput'
import { PasswordValidationInfo } from '@components/inputs/PasswordValidationInfo'
import { NavigationContainer } from '@screens/Common/containers'
import { NavigatorContext, useGoBack, useSnack, Validators } from '@utils'
import { userSelector } from '@src/selectors/app'

export const ChangePassword = () => {
  const styles = useStyleSheet(themedStyle)
  const dispatch = useDispatch()
  const goBack = useGoBack()
  const showSnack = useSnack()
  const insets = useSafeAreaInsets()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)

  const user = useSelector(userSelector)
  const email = user?.email

  const isSubmitting = useRef(false)
  const isResetting = useRef(false)

  const {
    control,
    formState: { errors, isValid },
    setFocus,
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      currentPassword: '',
      password: '',
    },
  })

  const password = useWatch({ control, name: 'password' })

  useEffect(
    () => () => {
      if (isSubmitting.current) {
        disableLoader()
        isSubmitting.current = false
      }
    },
    [disableLoader],
  )

  const onResetPress = () => {
    if (!email || isResetting.current) {
      return
    }

    isResetting.current = true

    dispatch({
      type: 'users/resetPasswordRequest',
      payload: { email },
      success: () => showSnack(`We've sent a password reset link to ${email}`),
      failure: () => showSnack('Failed to send reset link, please try later', null, 'error'),
      complete: () => {
        isResetting.current = false
      },
    })
  }

  const onSubmit = handleSubmit(({ currentPassword, password }) => {
    Keyboard.dismiss()

    if (isSubmitting.current) {
      return
    }

    isSubmitting.current = true
    enableLoader()

    dispatch({
      type: 'users/updatePassword',
      payload: {
        password,
        currentPassword,
      },
      success: () => {
        showSnack('Password updated')
        goBack()
      },
      failure: (error: any) => {
        showSnack(error?.message || 'Failed to update!', null, 'error')
      },
      complete: () => {
        disableLoader()
        isSubmitting.current = false
      },
    })
  })

  return (
    <NavigationContainer style={styles.container} title="Change Password" goBack={goBack}>
      <ScrollableAvoidKeyboard
        style={styles.container}
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 16 }]}
        keyboardShouldPersistTaps="handled"
      >
        <View>
          <View>
            <Controller
              control={control}
              name="currentPassword"
              rules={{ required: true }}
              render={({ field }) => (
                <SecureInput
                  {...field}
                  hasError={!!errors.currentPassword}
                  label="Current Password"
                  caption="Required to set a new password"
                  placeholder="Enter your current password"
                  returnKeyType="next"
                  style={styles.input}
                  onSubmitEditing={() => setFocus('password')}
                />
              )}
            />
            <TouchableOpacity
              accessibilityLabel="Forgot or don't have a password? Reset it"
              disabled={!email}
              onPress={onResetPress}
            >
              <Text type="small" bold style={styles.resetLink}>
                Forgot or don&apos;t have a password? Reset it
              </Text>
            </TouchableOpacity>
          </View>
          <Controller
            control={control}
            name="password"
            rules={{ required: true, validate: Validators.PasswordValidator }}
            render={({ field }) => (
              <SecureInput
                {...field}
                hasError={!!errors.password}
                label="New Password"
                placeholder="Set New Password"
                returnKeyType="done"
                style={styles.input}
                onSubmitEditing={onSubmit}
              />
            )}
          />
          <PasswordValidationInfo password={password} />
        </View>
        <Button
          type="primary"
          size="block"
          disabled={!isValid}
          onPress={onSubmit}
          accessibilityLabel="Save new password"
          allowPressWhenDisabled
        >
          Save New Password
        </Button>
      </ScrollableAvoidKeyboard>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  content: {
    justifyContent: 'space-between',
    alignSelf: 'center',
    width: '100%',
    maxWidth: 600,
    paddingHorizontal: 16,
  },
  input: {
    marginTop: 24,
    marginBottom: 16,
  },
  resetLink: {
    marginBottom: 8,
    color: 'theme.text.link',
  },
})
